import React from "react";
import { useHistory } from "react-router-dom";
import styled from "styled-components";
import {
  faBitbucket,
  faGithub,
  faGitlab,
  faJenkins,
  IconDefinition,
} from "@fortawesome/free-brands-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { Link } from "@components";
import { useBasePath, useUrlSearch } from "@hooks";

import { Heading } from "src/components/Headings";
import { StickerContainer } from "src/components/Sticker";

import { CIProvider } from "../types";

import AzureIcon from "@assets/inventory-icons/AzureIcon.png";
import BuildkiteIcon from "@assets/inventory-icons/BuildkiteIcon.png";
import CircleCIIcon from "@assets/inventory-icons/CircleCIIcon.png";

export const AddProjectsDialogWrapper = styled.div`
  padding: 16px 32px;
`;

export const AddProjectsDialogHeading = styled.div`
  margin: 8px;
`;

const ProjectTypeButtonContainer = styled(StickerContainer)`
  display: flex;
  cursor: pointer;
  flex-direction: row;
  align-items: center;
  gap: var(--global-spacing-m);
  margin: 8px;
  background-color: white;
  &:hover {
    color: #ffffff;
    background-color: var(--global-purple);
    & img {
      /* If there's an image, use filter to change color from black to white */
      filter: invert(92%) sepia(54%) saturate(2%) hue-rotate(87deg)
        brightness(108%) contrast(101%);
    }
  }
`;

interface ProjectTypeButtonProps {
  title: React.ReactNode;
  // either icon or image source and desc is defined
  icon?: IconDefinition;
  imgSrc?: string;
  imgDesc?: string;
  onClick?: React.MouseEventHandler;
}

export const ProjectTypeButton = ({
  title,
  icon,
  imgSrc,
  imgDesc,
  onClick,
}: ProjectTypeButtonProps) => (
  <ProjectTypeButtonContainer onClick={onClick}>
    {icon ? (
      <FontAwesomeIcon
        icon={icon}
        style={{ fontSize: "var(--global-font-size-xl)" }}
      />
    ) : (
      imgSrc &&
      imgDesc && <img src={imgSrc} alt={imgDesc} style={{ width: "28px" }} />
    )}
    <div
      style={{
        fontSize: "var(--global-font-size-m)",
        float: "right",
        width: "300px",
      }}
    >
      {title}
    </div>
  </ProjectTypeButtonContainer>
);

type ProjectTypeOption = {
  ciProvider: CIProvider;
  title: string;
  faIcon?: IconDefinition;
  imgSrc?: string;
  imgDesc?: string;
};

export function SetupProjectChooseType() {
  const { search } = useUrlSearch();
  const history = useHistory();
  const basePath = useBasePath();

  const getNextLink = (chosenType: CIProvider) => {
    if (chosenType === "github") {
      search.set("select-ci", "select-org");
      return history.location.pathname + "?" + search.toString();
    } else {
      return `${basePath}/setup/${chosenType}/manual`;
    }
  };

  const OPTIONS: ProjectTypeOption[] = [
    {
      ciProvider: "github",
      title: "GitHub Actions",
      faIcon: faGithub,
    },
    {
      ciProvider: "gitlab",
      title: "GitLab CI/CD",
      faIcon: faGitlab,
    },
    {
      ciProvider: "jenkins",
      title: "Jenkins",
      faIcon: faJenkins,
    },
    {
      ciProvider: "bitbucket",
      title: "Bitbucket",
      faIcon: faBitbucket,
    },
    {
      ciProvider: "circleci",
      title: "CircleCI",
      imgSrc: CircleCIIcon,
      imgDesc: "circle ci icon",
    },
    {
      ciProvider: "buildkite",
      title: "Buildkite",
      imgSrc: BuildkiteIcon,
      imgDesc: "buildkite icon",
    },
    {
      ciProvider: "azure",
      title: "Azure Pipelines",
      imgSrc: AzureIcon,
      imgDesc: "azure icon",
    },
  ];

  return (
    <AddProjectsDialogWrapper>
      <AddProjectsDialogHeading>
        <Heading>Add Semgrep to:</Heading>
      </AddProjectsDialogHeading>
      {OPTIONS.map((option) => (
        <Link to={getNextLink(option.ciProvider)}>
          <ProjectTypeButton
            title={option.title}
            icon={option.faIcon}
            imgSrc={option.imgSrc}
            imgDesc={option.imgDesc}
          />
        </Link>
      ))}
    </AddProjectsDialogWrapper>
  );
}
